import React from 'react';
import { motion } from 'framer-motion';
import { Incident, ResponseOption } from '../types/Incident';
import { ScoreCategory, InvestmentImpactDetails } from '../scoring/calculator';
import { useTheme } from '../contexts/ThemeContext';
import { useScoringConfiguration } from '../hooks/useScoringConfiguration';
import {
  mapImpactToBusinessCriteria,
  getBusinessImpactLabels,
  getBusinessImpactIcon,
  getBusinessImpactColor
} from '../utils/businessImpactCalculations';

/**
 * Props for IncidentResultsScreen component
 * 
 * Business context: This screen provides immediate feedback on how
 * the player's decision impacted the organization across four key
 * business dimensions. It's designed to create a learning moment
 * by clearly showing cause (decision) and effect (business impact).
 */
interface IncidentResultsScreenProps {
  incident: Incident;
  selectedResponse: ResponseOption;
  scoreImpact: ScoreCategory;
  isLastIncident?: boolean;
  onContinue: () => void;
  investmentImpactDetails?: InvestmentImpactDetails[];
}

const IncidentResultsScreen: React.FC<IncidentResultsScreenProps> = ({
  incident,
  selectedResponse,
  scoreImpact,
  isLastIncident = false,
  onContinue,
  investmentImpactDetails = []
}) => {
  const { isLight } = useTheme();
  const { getUIThresholds } = useScoringConfiguration();
  const uiThresholds = getUIThresholds();

  // Map the 4-category impact to 5 business criteria for consistency
  const businessCriteriaImpact = mapImpactToBusinessCriteria(scoreImpact);
  const businessLabels = getBusinessImpactLabels();

  return (
    <div className={`min-h-screen py-8 px-4 transition-colors duration-300 ${
      isLight 
        ? 'bg-gradient-to-br from-slate-50 to-slate-100' 
        : 'bg-gradient-to-br from-dark-primary to-dark-secondary'
    }`}>
      <motion.div
        className="max-w-4xl mx-auto"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {/* Header */}
        <motion.div 
          className="text-center mb-8"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
        >
          <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${isLight ? 'text-slate-900' : 'text-white'}`}>
            Response Results
          </h1>
          <p className={`text-lg ${isLight ? 'text-slate-600' : 'text-slate-300'}`}>
            Analysis of your incident response decision
          </p>
        </motion.div>

        {/* Incident Summary */}
        <motion.div
          className={`rounded-xl p-6 mb-6 ${
            isLight
              ? 'bg-white shadow-lg border border-slate-200'
              : 'bg-white/5 backdrop-blur-md border border-white/10'
          }`}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.3, duration: 0.5 }}
        >
          <h2 className={`text-xl font-semibold ${isLight ? 'text-slate-900' : 'text-white'}`}>Incident: {incident.title}</h2>
        </motion.div>

        {/* Your Response */}
        <motion.div
          className={`rounded-xl p-6 mb-6 ${
            isLight 
              ? 'bg-white shadow-lg border border-slate-200' 
              : 'bg-white/5 backdrop-blur-md border border-white/10'
          }`}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
        >
          <h2 className={`text-xl font-semibold mb-3 ${isLight ? 'text-slate-900' : 'text-white'}`}>Your Response</h2>
          <div className={`rounded-lg p-4 ${
            isLight 
              ? 'bg-professional-blue-50 border border-professional-blue-200' 
              : 'bg-professional-blue-500/10 border border-professional-blue-400/30'
          }`}>
            <h3 className={`text-lg font-medium mb-2 ${
              isLight ? 'text-professional-blue-700' : 'text-professional-blue-300'
            }`}>
              {selectedResponse.label}
            </h3>
            <p className={`${isLight ? 'text-slate-700' : 'text-slate-300'}`}>{selectedResponse.description}</p>
          </div>
        </motion.div>

        {/* Response Analysis - Benefits & Risks */}
        {(selectedResponse.benefits || selectedResponse.risks) && (
          <motion.div
            className={`rounded-xl p-6 mb-6 ${
              isLight
                ? 'bg-white shadow-lg border border-slate-200'
                : 'bg-white/5 backdrop-blur-md border border-white/10'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.5 }}
          >
            <h2 className={`text-xl font-semibold mb-4 ${isLight ? 'text-slate-900' : 'text-white'}`}>Response Analysis</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {selectedResponse.benefits && (
                <div className={`rounded-lg p-4 ${
                  isLight
                    ? 'bg-emerald-50 border border-emerald-200'
                    : 'bg-emerald-500/10 border border-emerald-400/30'
                }`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${
                    isLight ? 'text-emerald-700' : 'text-emerald-400'
                  }`}>Benefits</h3>
                  <p className={`text-sm ${isLight ? 'text-emerald-800' : 'text-emerald-200'}`}>
                    {selectedResponse.benefits}
                  </p>
                </div>
              )}
              {selectedResponse.risks && (
                <div className={`rounded-lg p-4 ${
                  isLight
                    ? 'bg-amber-50 border border-amber-200'
                    : 'bg-amber-500/10 border border-amber-400/30'
                }`}>
                  <h3 className={`text-sm font-semibold uppercase tracking-wider mb-2 ${
                    isLight ? 'text-amber-700' : 'text-amber-400'
                  }`}>Risks</h3>
                  <p className={`text-sm ${isLight ? 'text-amber-800' : 'text-amber-200'}`}>
                    {selectedResponse.risks}
                  </p>
                </div>
              )}
            </div>
          </motion.div>
        )}

        {/* Score Impact - hidden when benefits/risks are shown */}
        {!(selectedResponse.benefits || selectedResponse.risks) && <motion.div
          className={`rounded-xl p-6 mb-8 ${
            isLight
              ? 'bg-white shadow-lg border border-slate-200'
              : 'bg-white/5 backdrop-blur-md border border-white/10'
          }`}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.5 }}
        >
          <h2 className={`text-xl font-semibold mb-4 ${isLight ? 'text-slate-900' : 'text-white'}`}>Business Impact</h2>
          
          {/* Raw Impact Values (required by tests) */}
          <div className="mb-6">
            <h3 className={`text-lg font-medium mb-3 ${isLight ? 'text-slate-800' : 'text-slate-200'}`}>
              Core Impact Areas
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center">
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>Security</div>
                <div className={`text-lg font-semibold ${scoreImpact.security < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {scoreImpact.security}
                </div>
              </div>
              <div className="text-center">
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>Reputation</div>
                <div className={`text-lg font-semibold ${scoreImpact.reputation < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {scoreImpact.reputation}
                </div>
              </div>
              <div className="text-center">
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>Financial</div>
                <div className={`text-lg font-semibold ${scoreImpact.financial < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {scoreImpact.financial}
                </div>
              </div>
              <div className="text-center">
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>Operational</div>
                <div className={`text-lg font-semibold ${scoreImpact.operational < 0 ? 'text-red-600' : 'text-green-600'}`}>
                  {scoreImpact.operational}
                </div>
              </div>
            </div>
          </div>

          {/* Business Criteria (calculated from raw values) */}
          <div>
            <h3 className={`text-lg font-medium mb-3 ${isLight ? 'text-slate-800' : 'text-slate-200'}`}>
              Business Criteria Impact
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div className="text-center">
                <div className="text-2xl mb-2">{getBusinessImpactIcon('regulatoryCompliance')}</div>
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{businessLabels.regulatoryCompliance}</div>
                <div className={`text-lg font-semibold ${getBusinessImpactColor(businessCriteriaImpact.regulatoryCompliance, isLight)}`}>
                  {businessCriteriaImpact.regulatoryCompliance > 0 ? '+' : ''}{businessCriteriaImpact.regulatoryCompliance}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">{getBusinessImpactIcon('operationalResiliency')}</div>
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{businessLabels.operationalResiliency}</div>
                <div className={`text-lg font-semibold ${getBusinessImpactColor(businessCriteriaImpact.operationalResiliency, isLight)}`}>
                  {businessCriteriaImpact.operationalResiliency > 0 ? '+' : ''}{businessCriteriaImpact.operationalResiliency}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">{getBusinessImpactIcon('customerTrust')}</div>
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{businessLabels.customerTrust}</div>
                <div className={`text-lg font-semibold ${getBusinessImpactColor(businessCriteriaImpact.customerTrust, isLight)}`}>
                  {businessCriteriaImpact.customerTrust > 0 ? '+' : ''}{businessCriteriaImpact.customerTrust}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">{getBusinessImpactIcon('ipProtection')}</div>
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{businessLabels.ipProtection}</div>
                <div className={`text-lg font-semibold ${getBusinessImpactColor(businessCriteriaImpact.ipProtection, isLight)}`}>
                  {businessCriteriaImpact.ipProtection > 0 ? '+' : ''}{businessCriteriaImpact.ipProtection}
                </div>
              </div>
              <div className="text-center">
                <div className="text-2xl mb-2">{getBusinessImpactIcon('productIntegrity')}</div>
                <div className={`text-sm mb-1 ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>{businessLabels.productIntegrity}</div>
                <div className={`text-lg font-semibold ${getBusinessImpactColor(businessCriteriaImpact.productIntegrity, isLight)}`}>
                  {businessCriteriaImpact.productIntegrity > 0 ? '+' : ''}{businessCriteriaImpact.productIntegrity}
                </div>
              </div>
            </div>
          </div>
        </motion.div>}

        {/* Investment Impact Section */}
        {investmentImpactDetails.length > 0 && (
          <motion.div
            className={`rounded-xl p-6 mb-8 ${
              isLight
                ? 'bg-white shadow-lg border border-slate-200'
                : 'bg-white/5 backdrop-blur-md border border-white/10'
            }`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <h2 className={`text-xl font-semibold mb-4 ${isLight ? 'text-slate-900' : 'text-white'}`}>
              Your Investments in Action
            </h2>
            <div className="space-y-3">
              {investmentImpactDetails.map((detail, index) => (
                <motion.div
                  key={detail.investmentId}
                  className={`p-4 rounded-lg ${
                    isLight 
                      ? detail.synergyActive 
                        ? 'bg-emerald-50 border border-emerald-200' 
                        : 'bg-slate-50 border border-slate-200'
                      : detail.synergyActive
                        ? 'bg-emerald-500/10 border border-emerald-400/30'
                        : 'bg-white/5 border border-white/10'
                  }`}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1, duration: 0.3 }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className={`font-semibold ${
                        isLight 
                          ? detail.synergyActive ? 'text-emerald-700' : 'text-slate-800'
                          : detail.synergyActive ? 'text-emerald-300' : 'text-white'
                      }`}>
                        {detail.investmentName}
                      </h4>
                      {detail.synergyActive && detail.synergyDescription && (
                        <p className={`text-sm mt-1 ${isLight ? 'text-emerald-600' : 'text-emerald-400'}`}>
                          <span className="font-medium">SYNERGY BONUS:</span> {detail.synergyDescription}
                        </p>
                      )}
                    </div>
                    <div className="text-right ml-4">
                      <div className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                        Mitigation
                      </div>
                      <div className={`font-semibold ${
                        detail.synergyActive 
                          ? isLight ? 'text-emerald-600' : 'text-emerald-400'
                          : isLight ? 'text-slate-700' : 'text-slate-300'
                      }`}>
                        {detail.totalMitigation.toFixed(0)}%
                      </div>
                      {detail.synergyActive && detail.synergyBonus > 0 && (
                        <div className={`text-xs ${isLight ? 'text-emerald-500' : 'text-emerald-400'}`}>
                          (+{detail.synergyBonus.toFixed(0)}% bonus)
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className={`mt-4 pt-4 border-t ${
              isLight ? 'border-slate-200' : 'border-white/10'
            }`}>
              <div className="flex justify-between items-center">
                <span className={`text-sm ${isLight ? 'text-slate-600' : 'text-slate-400'}`}>
                  Total Damage Reduction:
                </span>
                <span className={`text-lg font-bold ${
                  isLight ? 'text-emerald-600' : 'text-emerald-400'
                }`}>
                  {Math.min(investmentImpactDetails.reduce((sum, d) => sum + d.totalMitigation, 0), (uiThresholds.mitigationCap?.high || 0.5) * 100).toFixed(0)}%
                </span>
              </div>
            </div>
          </motion.div>
        )}

        {/* Continue Button */}
        <motion.div
          className="text-center"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.9, duration: 0.5 }}
        >
          <button
            onClick={onContinue}
            className={`font-semibold py-4 px-8 rounded-lg text-lg hover:-translate-y-0.5 transition-all duration-200 uppercase tracking-wider ${
              isLight
                ? 'bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white shadow-lg hover:shadow-xl'
                : 'bg-gradient-to-r from-professional-blue-600 to-professional-blue-700 text-white shadow-lg hover:shadow-xl'
            }`}
          >
            {isLastIncident ? 'View Final Results' : 'Continue to Next Incident'}
          </button>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default IncidentResultsScreen;